import React, { useState, useEffect, useCallback, useRef } from 'react';
import { searchCompetitor as searchCompany } from "../../../../services/accIqService";
import './FinancialDetails.css';

const FinancialDetails = ({
    value,
    ciqId,
    onChange,
    error,
    disabled
}) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSelected, setIsSelected] = useState(!!ciqId);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);

    // Update input when value prop changes
    useEffect(() => {
        setInputValue(value || '');
        // Consider selected if ciqId exists OR if value (company name) is set
        setIsSelected(!!ciqId || !!value);
    }, [value, ciqId]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search
    const searchCompanies = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setSuggestions([]);
            return;
        }

        setIsLoading(true);
        try {
            const results = await searchCompany(query);
            setSuggestions(results);
            setShowDropdown(true);
        } catch (err) {
            setSuggestions([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setIsSelected(false);

        // Clear previous selection if user types
        if (ciqId) {
            onChange('', '');
        }

        // Debounce API call
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            searchCompanies(newValue);
        }, 300);
    };

    const handleSelect = (company) => {
        setInputValue(company.companyname);
        setIsSelected(true);
        setShowDropdown(false);
        setSuggestions([]);
        onChange(company.companyname, company.companyid || '');
    };

    const handleClear = () => {
        setInputValue('');
        setIsSelected(false);
        setSuggestions([]);
        onChange('', '');
    };

    const handleFocus = () => {
        if (suggestions.length > 0 && !isSelected) {
            setShowDropdown(true);
        }
    };

    return (
        <div className="financial-details">
            <label className="financial-details-label">Company financials and documents *</label>
            <div className="financial-details-input-wrapper" ref={wrapperRef}>
                <input
                    type="text"
                    className={`financial-details-input ${error ? 'error' : ''} ${isSelected ? 'selected' : ''}`}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder="Search company name..."
                    disabled={disabled}
                />

                {showDropdown && !isSelected && (
                    <div className="financial-details-dropdown">
                        {isLoading ? (
                            <div className="financial-details-loading">Searching...</div>
                        ) : suggestions.length > 0 ? (
                            suggestions.map((company, index) => {
                                const hasNoCiq = !company.companyid || company.companyname === 'Data Unavailable';
                                return (
                                    <div
                                        key={`${company.companyid || 'no-ciq'}-${index}`}
                                        className={`financial-details-dropdown-item ${hasNoCiq ? 'no-ciq' : ''}`}
                                        onClick={() => {
                                            if (hasNoCiq) {
                                                // Use the typed input as the company name
                                                setIsSelected(true);
                                                setShowDropdown(false);
                                                setSuggestions([]);
                                                onChange(inputValue, '');
                                            } else {
                                                handleSelect(company);
                                            }
                                        }}
                                    >
                                        {hasNoCiq
                                            ? <span className="financial-details-continue-action">Continue with this company</span>
                                            : <span>{company.companyname}</span>
                                        }
                                    </div>
                                );
                            })
                        ) : inputValue.length >= 2 ? (
                            <div className="financial-details-loading">No companies found</div>
                        ) : null}
                    </div>
                )}
            </div>

            {isSelected && (
                <div className={`financial-details-selected ${!ciqId ? 'no-ciq' : ''}`}>
                    <span>✓ Selected: {inputValue}{!ciqId && <span className="financial-details-no-ciq-badge"> (No company financials and documents)</span>}</span>
                    <button
                        type="button"
                        className="financial-details-selected-clear"
                        onClick={handleClear}
                        disabled={disabled}
                    >
                        ×
                    </button>
                </div>
            )}

            {error && <div className="financial-details-error">{error}</div>}
        </div>
    );
};

export default FinancialDetails;
